/**
 * Decoding, the decoded-sample cache, and an honest answer to "what can this browser open?".
 *
 * Two things here are less obvious than they look.
 *
 * **`decodeAudioData` has two shapes.** The modern one returns a promise; the original one
 * only calls back, returns `undefined`, and is still what some WebKit builds do. Awaiting the
 * return value of the old form gives you `undefined` forever, which reads to a user as an
 * import that silently never finishes, so both shapes are wired to the same settlement here.
 * It also *detaches* the ArrayBuffer it is given, which is why `retainSource` exists.
 *
 * **Codec support cannot be assumed from a file extension.** `probeCodecSupport` decodes real
 * generated files for the three containers this file can author correctly (WAV, AIFF, CAF)
 * and falls back to the media-capability APIs for the rest, labelling every row with how it
 * was established. A capability matrix built on guesses is worse than no matrix.
 */

import type { UUID } from '../model/types';

// MARK: - Errors

export type DecodeFailureKind = 'unsupportedFormat' | 'corrupt' | 'empty' | 'tooLarge';

/**
 * A decode failure the UI can show verbatim. The wording matches `AudioEngineError` in the
 * native build so the two platforms say the same thing about the same file.
 */
export class AudioDecodeError extends Error {
  readonly kind: DecodeFailureKind;
  readonly fileName: string;
  readonly recoverySuggestion: string;
  /** Container sniffed from the first bytes, when one was recognised. */
  readonly detectedContainer: string | null;

  constructor(
    kind: DecodeFailureKind,
    fileName: string,
    message: string,
    recoverySuggestion: string,
    detectedContainer: string | null = null,
  ) {
    super(message);
    this.name = 'AudioDecodeError';
    this.kind = kind;
    this.fileName = fileName;
    this.recoverySuggestion = recoverySuggestion;
    this.detectedContainer = detectedContainer;
  }
}

// MARK: - Limits
//
// Mirrors `AudioLimits` in the native build. Kept here rather than in the model because they
// are decisions about *this* platform's memory, not about the project format.

/** Decoded-audio budget before the cache starts evicting. */
export const CACHE_BYTE_BUDGET = 192 * 1024 * 1024;
/** Largest file we will hand to the decoder at all. */
export const MAX_IMPORT_BYTES = 512 * 1024 * 1024;

/**
 * Longest recording we will decode into memory.
 *
 * `decodeAudioData` has no streaming form, so a decoded buffer costs
 * `duration × rate × channels × 4` bytes whatever the file it came from weighs: half an hour
 * of stereo at 48 kHz is already 690 MB of Float32. A three-hour podcast inside a 40 MB MP3
 * passes every byte-size check there is and then exhausts the tab.
 */
export const MAX_IMPORT_SECONDS = 30 * 60;

/**
 * Asks the media stack how long a file is without decoding it.
 *
 * This is the cross-check the byte-size limit cannot make: an hour of speech in a 30 MB MP3
 * is a small file and an enormous buffer. `HTMLMediaElement` parses only enough of the
 * container to answer, so the whole check costs a few hundred kilobytes of read. Returns
 * `null` when the element cannot say — a live stream, an exotic container, no element at all
 * — and the caller falls back to the post-decode check.
 */
export function probeMediaDuration(blob: Blob, timeoutMs = 5000): Promise<number | null> {
  if (typeof document === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return Promise.resolve(null);
  }
  return new Promise<number | null>((resolve) => {
    const url = URL.createObjectURL(blob);
    const element = document.createElement('audio');
    let settled = false;
    const finish = (value: number | null): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      element.removeAttribute('src');
      element.load();
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = setTimeout(() => finish(null), timeoutMs);
    element.preload = 'metadata';
    element.onloadedmetadata = (): void => {
      const seconds = element.duration;
      finish(Number.isFinite(seconds) && seconds > 0 ? seconds : null);
    };
    element.onerror = (): void => finish(null);
    element.src = url;
  });
}

/** The error a file that is simply too long should produce, wherever it is caught. */
export function tooLongError(fileName: string, seconds: number): AudioDecodeError {
  return new AudioDecodeError(
    'tooLarge',
    fileName,
    `“${fileName}” is ${Math.round(seconds / 60)} minutes long, past the ${Math.round(MAX_IMPORT_SECONDS / 60)}-minute import limit.`,
    'Export a shorter section and import that instead. PadLab has to hold the whole sound in memory to edit it.',
  );
}

/** Float32 per channel per frame — what a decoded buffer actually costs in memory. */
export function estimateDecodedBytes(frameCount: number, channelCount: number): number {
  return Math.max(0, Math.round(frameCount)) * Math.max(1, Math.round(channelCount)) * 4;
}

// MARK: - Container sniffing

/**
 * Names the container from its magic bytes. Used to make an error specific ("that is a FLAC
 * file this browser will not open") instead of generic, and to catch a `.wav` that is really
 * an MP3.
 */
export function sniffContainer(bytes: ArrayBuffer): string | null {
  if (bytes.byteLength < 12) return null;
  const head = new Uint8Array(bytes, 0, 12);
  const ascii = (offset: number, length: number): string => {
    let text = '';
    for (let i = 0; i < length; i++) text += String.fromCharCode(head[offset + i] ?? 0);
    return text;
  };

  const first4 = ascii(0, 4);
  if (first4 === 'RIFF' && ascii(8, 4) === 'WAVE') return 'WAV';
  if (first4 === 'FORM') {
    const form = ascii(8, 4);
    if (form === 'AIFF') return 'AIFF';
    if (form === 'AIFC') return 'AIFF-C';
  }
  if (first4 === 'caff') return 'CAF';
  if (first4 === 'fLaC') return 'FLAC';
  if (first4 === 'OggS') return 'Ogg';
  if (ascii(4, 4) === 'ftyp') return 'MP4/M4A';
  if (first4 === 'ID3') return 'MP3';
  if (head[0] === 0x1a && head[1] === 0x45 && head[2] === 0xdf && head[3] === 0xa3) {
    return 'WebM/Matroska';
  }
  // A bare MPEG audio frame: 11 sync bits with a valid layer field.
  if (head[0] === 0xff && ((head[1] ?? 0) & 0xe0) === 0xe0) {
    return ((head[1] ?? 0) & 0x06) === 0 ? 'AAC (ADTS)' : 'MP3';
  }
  return null;
}

// MARK: - Decoding

export interface DecodeOptions {
  /**
   * Copy the bytes before decoding. `decodeAudioData` detaches its input, so pass this when
   * the caller still needs the encoded file afterwards.
   */
  retainSource?: boolean;
}

/**
 * Bridges the promise and callback forms of `decodeAudioData`. Both are wired to one
 * settlement guard, because a browser that implements both will fire both.
 */
function decodeEitherWay(context: BaseAudioContext, bytes: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    let settled = false;
    const succeed = (buffer: AudioBuffer): void => {
      if (settled) return;
      settled = true;
      resolve(buffer);
    };
    const fail = (cause: unknown): void => {
      if (settled) return;
      settled = true;
      reject(cause ?? new Error('the decoder gave no reason'));
    };

    let returned: unknown;
    try {
      returned = context.decodeAudioData(bytes, succeed, fail as (error: DOMException) => void);
    } catch (cause) {
      fail(cause);
      return;
    }
    if (returned instanceof Promise) returned.then(succeed, fail);
  });
}

function describeCause(cause: unknown): string {
  if (cause instanceof DOMException) return cause.message !== '' ? cause.message : cause.name;
  if (cause instanceof Error) return cause.message;
  return 'the decoder gave no reason';
}

/**
 * Decodes one file. Rejects with `AudioDecodeError`, never with a raw `DOMException`, so
 * every caller has a sentence it can put on screen.
 */
export async function decodeAudio(
  context: BaseAudioContext,
  bytes: ArrayBuffer,
  fileName: string,
  options: DecodeOptions = {},
): Promise<AudioBuffer> {
  if (bytes.byteLength === 0) {
    throw new AudioDecodeError(
      'empty',
      fileName,
      `“${fileName}” contains no audio.`,
      'Choose a different file, or record a new take.',
    );
  }
  if (bytes.byteLength > MAX_IMPORT_BYTES) {
    throw new AudioDecodeError(
      'tooLarge',
      fileName,
      `“${fileName}” is larger than the ${Math.round(MAX_IMPORT_BYTES / (1024 * 1024))} MB import limit.`,
      'Trim the file, or export a shorter section, and import it again.',
    );
  }

  const container = sniffContainer(bytes);
  const payload = options.retainSource === true ? bytes.slice(0) : bytes;

  let buffer: AudioBuffer;
  try {
    buffer = await decodeEitherWay(context, payload);
  } catch (cause) {
    const reason = describeCause(cause);
    const named = container === null ? '' : ` It looks like a ${container} file.`;
    throw new AudioDecodeError(
      'unsupportedFormat',
      fileName,
      `“${fileName}” uses an audio format this browser cannot decode: ${reason}.${named}`,
      'Convert the file to WAV or M4A and import it again.',
      container,
    );
  }

  if (buffer.duration > MAX_IMPORT_SECONDS) throw tooLongError(fileName, buffer.duration);

  if (buffer.length === 0 || buffer.numberOfChannels === 0) {
    throw new AudioDecodeError(
      'empty',
      fileName,
      `“${fileName}” decoded to no audio at all.`,
      'The file may be truncated. Convert it to WAV or M4A and import it again.',
      container,
    );
  }
  return buffer;
}

// MARK: - Decoded samples

export interface DecodedSample {
  readonly assetID: UUID;
  /** One Float32Array per channel. Mono samples expose a single entry. */
  readonly channels: readonly Float32Array[];
  readonly sampleRate: number;
  readonly frameCount: number;
  readonly duration: number;
  readonly byteSize: number;
  /** Largest absolute sample value — what `SampleEdit.isNormalised` is measured against. */
  readonly peak: number;
  /**
   * True when `channels` are views onto a `SharedArrayBuffer`, so every pad worklet that uses
   * this asset reads the same memory instead of holding its own copy.
   */
  readonly shared: boolean;
  /** Retained only on the `AudioBufferSourceNode` fallback path, which needs the real buffer. */
  readonly buffer: AudioBuffer | null;
}

/** True when this page may allocate shared memory the audio worklets can read without a copy. */
export function canShareAudioMemory(): boolean {
  return (
    typeof SharedArrayBuffer === 'function' &&
    typeof globalThis.crossOriginIsolated === 'boolean' &&
    globalThis.crossOriginIsolated
  );
}

export interface DecodedSampleOptions {
  /** Keep the `AudioBuffer` alive — required when playback uses `AudioBufferSourceNode`. */
  retainBuffer?: boolean;
  /** Copy channel data into shared memory when the page is cross-origin isolated. */
  preferShared?: boolean;
}

/**
 * Turns a decoded `AudioBuffer` into the form the engine keeps.
 *
 * On the worklet path the `AudioBuffer` itself is dropped: `getChannelData` already hands
 * back the underlying Float32Array without copying, so holding both would double the memory
 * for no benefit. When shared memory is available the data is copied once into a
 * `SharedArrayBuffer` — that copy pays for itself the moment a second pad uses the same
 * asset, and it is the only way a worklet can read the samples without its own duplicate.
 */
export function decodedSampleFromBuffer(
  assetID: UUID,
  buffer: AudioBuffer,
  options: DecodedSampleOptions = {},
): DecodedSample {
  const frameCount = buffer.length;
  const channelCount = buffer.numberOfChannels;
  const useShared = options.preferShared === true && canShareAudioMemory();

  const channels: Float32Array[] = [];
  let peak = 0;
  for (let channel = 0; channel < channelCount; channel++) {
    const source = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      const magnitude = Math.abs(source[i]!);
      if (magnitude > peak) peak = magnitude;
    }
    if (useShared) {
      const shared = new SharedArrayBuffer(frameCount * 4);
      const view = new Float32Array(shared);
      view.set(source);
      channels.push(view);
    } else {
      channels.push(source);
    }
  }

  return {
    assetID,
    channels,
    sampleRate: buffer.sampleRate,
    frameCount,
    duration: buffer.duration,
    byteSize: estimateDecodedBytes(frameCount, channelCount),
    peak,
    shared: useShared,
    buffer: options.retainBuffer === true ? buffer : null,
  };
}

// MARK: - Cache

/**
 * Least-recently-used cache of decoded audio, bounded by bytes rather than by entry count
 * because one five-minute track costs as much as a hundred drum hits.
 *
 * Pads that are on screen pin their assets. A pinned entry is never evicted, so a trigger can
 * never arrive to find its samples gone — which is the whole reason nothing decodes at
 * trigger time.
 */
export class DecodedSampleCache {
  private readonly entries = new Map<UUID, DecodedSample>();
  private readonly pinned = new Set<UUID>();
  private bytes = 0;

  /** Called for every sample the cache drops, so the engine can tell the worklets. */
  onEvict: ((sample: DecodedSample) => void) | null = null;

  constructor(readonly byteBudget: number = CACHE_BYTE_BUDGET) {}

  get byteSize(): number {
    return this.bytes;
  }

  get size(): number {
    return this.entries.size;
  }

  has(assetID: UUID): boolean {
    return this.entries.has(assetID);
  }

  /** Reads and marks as most recently used. */
  get(assetID: UUID): DecodedSample | undefined {
    const sample = this.entries.get(assetID);
    if (sample === undefined) return undefined;
    this.entries.delete(assetID);
    this.entries.set(assetID, sample);
    return sample;
  }

  /** Reads without disturbing the eviction order. */
  peek(assetID: UUID): DecodedSample | undefined {
    return this.entries.get(assetID);
  }

  pin(assetID: UUID): void {
    this.pinned.add(assetID);
  }

  unpin(assetID: UUID): void {
    this.pinned.delete(assetID);
  }

  put(sample: DecodedSample): void {
    const existing = this.entries.get(sample.assetID);
    if (existing !== undefined) this.bytes -= existing.byteSize;
    this.entries.set(sample.assetID, sample);
    this.bytes += sample.byteSize;
    this.trim();
  }

  delete(assetID: UUID): boolean {
    const sample = this.entries.get(assetID);
    if (sample === undefined) return false;
    this.entries.delete(assetID);
    this.pinned.delete(assetID);
    this.bytes -= sample.byteSize;
    return true;
  }

  clear(): void {
    this.entries.clear();
    this.pinned.clear();
    this.bytes = 0;
  }

  /**
   * Drops least-recently-used unpinned entries until the budget is met. Returns how many bytes
   * it managed to free — which can be zero if everything resident is pinned, and the caller
   * has to decide what that means rather than being told a comforting lie.
   */
  private trim(): number {
    if (this.bytes <= this.byteBudget) return 0;
    let freed = 0;
    for (const [assetID, sample] of this.entries) {
      if (this.bytes <= this.byteBudget) break;
      if (this.pinned.has(assetID)) continue;
      this.entries.delete(assetID);
      this.bytes -= sample.byteSize;
      freed += sample.byteSize;
      this.onEvict?.(sample);
    }
    return freed;
  }
}

// MARK: - Codec probing

export type CodecProbeMethod = 'decoded' | 'declared' | 'unavailable';

export interface CodecProbeEntry {
  readonly id: string;
  readonly label: string;
  readonly mimeType: string;
  readonly extensions: readonly string[];
  readonly supported: boolean;
  /** `decoded` means a real file went through `decodeAudioData` and came back as audio. */
  readonly method: CodecProbeMethod;
  readonly detail: string;
}

export interface CodecProbeReport {
  readonly sampleRate: number;
  readonly entries: readonly CodecProbeEntry[];
  /** Extensions the import picker can offer with confidence. */
  readonly supportedExtensions: readonly string[];
  /** True when at least one row was established by actually decoding something. */
  readonly hasVerifiedRow: boolean;
}

const PROBE_RATE = 8000;
const PROBE_FRAMES = 400;

/** A quiet, non-silent tone — a decoder that returns all zeros has not really decoded. */
function probeSamples(): Int16Array {
  const samples = new Int16Array(PROBE_FRAMES);
  for (let i = 0; i < PROBE_FRAMES; i++) {
    samples[i] = Math.round(Math.sin((i / PROBE_RATE) * 2 * Math.PI * 440) * 8000);
  }
  return samples;
}

function writeAscii(view: DataView, offset: number, text: string): void {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
}

/** IEEE 754 80-bit extended, which is the only way AIFF stores a sample rate. */
function writeExtended80(view: DataView, offset: number, value: number): void {
  if (value <= 0) {
    for (let i = 0; i < 10; i++) view.setUint8(offset + i, 0);
    return;
  }
  const exponent = Math.floor(Math.log2(value));
  const mantissa = value / Math.pow(2, exponent); // 1 ≤ mantissa < 2
  view.setUint16(offset, 16383 + exponent);
  // Split the 64-bit mantissa into two 32-bit halves: JavaScript numbers cannot hold it whole.
  const high = Math.floor(mantissa * 0x80000000);
  const low = Math.floor((mantissa * 0x80000000 - high) * 0x100000000);
  view.setUint32(offset + 2, high >>> 0);
  view.setUint32(offset + 6, low >>> 0);
}

function buildWav(): ArrayBuffer {
  const samples = probeSamples();
  const dataBytes = samples.length * 2;
  const bytes = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(bytes);
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, PROBE_RATE, true);
  view.setUint32(28, PROBE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i++) view.setInt16(44 + i * 2, samples[i]!, true);
  return bytes;
}

function buildAiff(): ArrayBuffer {
  const samples = probeSamples();
  const dataBytes = samples.length * 2;
  const bytes = new ArrayBuffer(54 + dataBytes);
  const view = new DataView(bytes);
  writeAscii(view, 0, 'FORM');
  view.setUint32(4, 46 + dataBytes);
  writeAscii(view, 8, 'AIFF');
  writeAscii(view, 12, 'COMM');
  view.setUint32(16, 18);
  view.setInt16(20, 1); // mono
  view.setUint32(22, samples.length);
  view.setInt16(26, 16);
  writeExtended80(view, 28, PROBE_RATE);
  writeAscii(view, 38, 'SSND');
  view.setUint32(42, 8 + dataBytes);
  view.setUint32(46, 0); // offset
  view.setUint32(50, 0); // block size
  for (let i = 0; i < samples.length; i++) view.setInt16(54 + i * 2, samples[i]!);
  return bytes;
}

function buildCaf(): ArrayBuffer {
  const samples = probeSamples();
  const dataBytes = samples.length * 2;
  const bytes = new ArrayBuffer(8 + 12 + 32 + 12 + 4 + dataBytes);
  const view = new DataView(bytes);
  writeAscii(view, 0, 'caff');
  view.setUint16(4, 1); // version
  view.setUint16(6, 0); // flags

  writeAscii(view, 8, 'desc');
  view.setBigInt64(12, 32n);
  view.setFloat64(20, PROBE_RATE);
  writeAscii(view, 28, 'lpcm');
  view.setUint32(32, 0); // signed integer, big-endian
  view.setUint32(36, 2); // bytes per packet
  view.setUint32(40, 1); // frames per packet
  view.setUint32(44, 1); // channels
  view.setUint32(48, 16); // bits per channel

  writeAscii(view, 52, 'data');
  view.setBigInt64(56, BigInt(4 + dataBytes));
  view.setUint32(64, 0); // edit count
  for (let i = 0; i < samples.length; i++) view.setInt16(68 + i * 2, samples[i]!);
  return bytes;
}

interface DeclaredCandidate {
  id: string;
  label: string;
  mimeType: string;
  extensions: string[];
}

const DECLARED_CANDIDATES: readonly DeclaredCandidate[] = [
  { id: 'mp3', label: 'MP3', mimeType: 'audio/mpeg', extensions: ['.mp3'] },
  {
    id: 'aac-m4a',
    label: 'AAC in M4A',
    mimeType: 'audio/mp4; codecs="mp4a.40.2"',
    extensions: ['.m4a', '.mp4', '.aac'],
  },
  { id: 'alac', label: 'Apple Lossless', mimeType: 'audio/mp4; codecs="alac"', extensions: ['.m4a'] },
  { id: 'flac', label: 'FLAC', mimeType: 'audio/flac', extensions: ['.flac'] },
  {
    id: 'ogg-vorbis',
    label: 'Ogg Vorbis',
    mimeType: 'audio/ogg; codecs="vorbis"',
    extensions: ['.ogg', '.oga'],
  },
  {
    id: 'webm-opus',
    label: 'Opus in WebM',
    mimeType: 'audio/webm; codecs="opus"',
    extensions: ['.webm'],
  },
  {
    id: 'ogg-opus',
    label: 'Opus in Ogg',
    mimeType: 'audio/ogg; codecs="opus"',
    extensions: ['.opus', '.ogg'],
  },
];

function declaredSupport(mimeType: string): { supported: boolean; detail: string } {
  const media = globalThis.MediaSource;
  if (media !== undefined && typeof media.isTypeSupported === 'function') {
    const supported = media.isTypeSupported(mimeType);
    return {
      supported,
      detail: supported
        ? 'Declared by MediaSource.isTypeSupported. Not verified by decoding a file.'
        : 'MediaSource.isTypeSupported says no.',
    };
  }
  if (typeof document === 'undefined') {
    return { supported: false, detail: 'No way to ask this context about media types.' };
  }
  const probe = document.createElement('audio');
  const answer = probe.canPlayType(mimeType);
  if (answer === 'probably') {
    return { supported: true, detail: 'canPlayType says “probably”. Not verified by decoding.' };
  }
  if (answer === 'maybe') {
    return {
      supported: true,
      detail: 'canPlayType says “maybe”: playback is likely, decoding is not certain.',
    };
  }
  return { supported: false, detail: 'canPlayType says no.' };
}

/**
 * Reports what this browser will actually open.
 *
 * WAV, AIFF and CAF are established by decoding a real generated file, which is the only kind
 * of answer worth putting in a capability matrix. The compressed formats cannot be authored
 * in a few hundred bytes, so they fall back to the media-capability APIs — which describe
 * *playback* rather than `decodeAudioData`, and every such row says so.
 */
export async function probeCodecSupport(context: BaseAudioContext): Promise<CodecProbeReport> {
  const entries: CodecProbeEntry[] = [];
  const supportedExtensions = new Set<string>();
  let hasVerifiedRow = false;

  const generated: { id: string; label: string; mimeType: string; extensions: string[]; bytes: ArrayBuffer }[] =
    [
      {
        id: 'wav',
        label: 'WAV (16-bit PCM)',
        mimeType: 'audio/wav',
        extensions: ['.wav', '.wave'],
        bytes: buildWav(),
      },
      {
        id: 'aiff',
        label: 'AIFF (16-bit PCM)',
        mimeType: 'audio/aiff',
        extensions: ['.aif', '.aiff'],
        bytes: buildAiff(),
      },
      {
        id: 'caf',
        label: 'CAF (16-bit PCM)',
        mimeType: 'audio/x-caf',
        extensions: ['.caf'],
        bytes: buildCaf(),
      },
    ];

  for (const candidate of generated) {
    try {
      const buffer = await decodeEitherWay(context, candidate.bytes);
      const ok = buffer.length > 0;
      if (ok) {
        hasVerifiedRow = true;
        for (const extension of candidate.extensions) supportedExtensions.add(extension);
      }
      entries.push({
        id: candidate.id,
        label: candidate.label,
        mimeType: candidate.mimeType,
        extensions: candidate.extensions,
        supported: ok,
        method: 'decoded',
        detail: ok
          ? `Verified: a generated ${candidate.label} file decoded to ${buffer.length} frames.`
          : 'Decoded, but produced no frames.',
      });
    } catch {
      // A generated file that will not decode is still useful information, but the media
      // capability APIs get the last word in case the failure is this file rather than the
      // container.
      const declared = declaredSupport(candidate.mimeType);
      if (declared.supported) {
        for (const extension of candidate.extensions) supportedExtensions.add(extension);
      }
      entries.push({
        id: candidate.id,
        label: candidate.label,
        mimeType: candidate.mimeType,
        extensions: candidate.extensions,
        supported: declared.supported,
        method: declared.supported ? 'declared' : 'unavailable',
        detail: `decodeAudioData rejected a generated ${candidate.label} file. ${declared.detail}`,
      });
    }
  }

  for (const candidate of DECLARED_CANDIDATES) {
    const declared = declaredSupport(candidate.mimeType);
    if (declared.supported) {
      for (const extension of candidate.extensions) supportedExtensions.add(extension);
    }
    entries.push({
      id: candidate.id,
      label: candidate.label,
      mimeType: candidate.mimeType,
      extensions: candidate.extensions,
      supported: declared.supported,
      method: declared.supported ? 'declared' : 'unavailable',
      detail: declared.detail,
    });
  }

  return {
    sampleRate: context.sampleRate,
    entries,
    supportedExtensions: [...supportedExtensions].sort(),
    hasVerifiedRow,
  };
}
